import { useState } from "react"
import { Shell } from "../components/shell"
import { Button, Input, Textarea, Label, Select } from "../components/ui"
import { authClient } from "../lib/auth-client"
import { CheckCircle2, LifeBuoy } from "lucide-react"

const CATEGORIES = [
  { value: "account", label: "Account & Login" },
  { value: "billing", label: "Billing & Plans" },
  { value: "bug", label: "Bug Report" },
  { value: "feature", label: "Feature Request" },
  { value: "other", label: "Other" },
]

export default function SupportPage() {
  const { data: session } = authClient.useSession()
  const [name, setName] = useState(session?.user.name ?? "")
  const [email, setEmail] = useState(session?.user.email ?? "")
  const [category, setCategory] = useState("other")
  const [message, setMessage] = useState("")
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle")
  const [error, setError] = useState("")

  const submit = async () => {
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError("Please fill in all fields.")
      return
    }
    setStatus("sending")
    setError("")
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          message,
          category: CATEGORIES.find(c => c.value === category)?.label ?? category,
          userId: session?.user.id,
          userEmail: session?.user.email,
        }),
      })
      if (!res.ok) {
        const data = await res.json<{ error: string }>()
        throw new Error(data.error)
      }
      setStatus("sent")
      setMessage("")
    } catch (e: any) {
      setError(e.message || "Something went wrong. Please try again.")
      setStatus("error")
    }
  }

  return (
    <Shell title="Support">
      <div className="mx-auto w-full max-w-xl px-4 py-10">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <LifeBuoy className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Contact Support</h1>
            <p className="text-sm text-muted-foreground">We'll get back to you as soon as possible.</p>
          </div>
        </div>

        {status === "sent" ? (
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-border bg-card p-10 text-center">
            <CheckCircle2 className="h-12 w-12 text-primary" />
            <h2 className="text-lg font-semibold">Message sent!</h2>
            <p className="text-sm text-muted-foreground">Thanks for reaching out. Our team will review your message and respond shortly.</p>
            <Button variant="outline" onClick={() => setStatus("idle")}>Send another</Button>
          </div>
        ) : (
          <div className="space-y-5 rounded-2xl border border-border bg-card p-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" type="email" />
              </div>
            </div>
            <div>
              <Label>Category</Label>
              <Select
                value={category}
                onChange={e => setCategory(e.target.value)}
                options={CATEGORIES}
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Describe your issue or question in as much detail as possible..."
                rows={6}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button onClick={submit} isLoading={status === "sending"} className="w-full">
              Send message
            </Button>
          </div>
        )}
      </div>
    </Shell>
  )
}
